import React, { useCallback, useEffect, useState } from 'react';
import socket from '../socket';
import useSocketEvent from '../utils/useSocketEvent';
import { fetchJson } from '../utils/api';
import Button from './ui/Button';
import './css/Finalfrage.css';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const PHASE_LABEL = {
    category: 'Kategorie',
    wager: 'Einsätze',
    question: 'Frage',
    countdown: 'Countdown',
    locked: 'gesperrt',
    revealPicks: 'Antworten aufdecken',
    revealSolution: 'Lösung',
    resolved: 'abgerechnet',
};

// Live-Steuerung (Moderator) für das Finalfrage-Finale.
function FinaleFinalfrageRegie() {
    const [finale, setFinale] = useState(null);
    const [teams, setTeams] = useState([]);
    const [showSolution, setShowSolution] = useState(false);
    const [showCorrections, setShowCorrections] = useState(false);
    const [wagerInputs, setWagerInputs] = useState({});

    const loadTeams = useCallback(async () => {
        try {
            const data = await fetchJson('/api/teams');
            setTeams(Array.isArray(data) ? data.filter((t) => (t.name || '').toLowerCase() !== 'moderator') : []);
        } catch (_) { /* ignore */ }
    }, []);

    const register = useCallback(() => {
        socket.emit('finale:registerControl');
        socket.emit('finale:state:request');
    }, []);

    useSocketEvent(socket, 'connect', register);
    useSocketEvent(socket, 'teamsUpdated', loadTeams);
    useSocketEvent(socket, 'teamCreated', loadTeams);
    useSocketEvent(socket, 'teamDeleted', loadTeams);
    useSocketEvent(socket, 'finale:state', useCallback(({ finale: f }) => {
        if (f?.kind === 'finalfrage') setFinale(f);
    }, []));

    useEffect(() => {
        loadTeams();
        register();
        return () => socket.emit('finale:unregisterControl');
    }, [loadTeams, register]);

    const emit = (event, payload) => socket.emit(event, payload);
    const teamName = (id) => teams.find((t) => String(t.id) === String(id))?.name || `Team ${id}`;

    if (!finale) {
        return (
            <div className="card section-stack ff-regie">
                <div className="text-muted">Regie wird geladen…</div>
            </div>
        );
    }

    const live = finale.live || null;
    const phase = live?.phase || null;
    const active = !!finale.active;
    const question = finale.question || {};
    const answers = Array.isArray(question.answers) ? question.answers : [];
    const revealedCount = live?.revealedAnswerCount ?? 0;
    const perTeamIds = live ? Object.keys(live.perTeam) : [];
    const countdownRunning = phase === 'countdown';

    // ── Ein progressiver Hauptbutton ──
    const primaryAction = (() => {
        if (!active || !live) {
            return { label: 'Finale starten', onClick: () => emit('finalfrage:toggle', { active: true }) };
        }
        switch (phase) {
            case 'category':
                return live.categoryVisible
                    ? { label: 'Einsätze freigeben', onClick: () => emit('finalfrage:setPhase', { phase: 'wager' }) }
                    : { label: 'Kategorie anzeigen', onClick: () => emit('finalfrage:setCategoryVisible', { visible: true }) };
            case 'wager':
                return { label: 'Frage anzeigen', onClick: () => emit('finalfrage:setPhase', { phase: 'question' }) };
            case 'question':
                return { label: 'Countdown starten', onClick: () => emit('finalfrage:startCountdown') };
            case 'countdown':
                return null;
            case 'locked':
                return {
                    label: 'Team-Antworten aufdecken',
                    onClick: () => {
                        emit('finalfrage:revealAllPicks', { revealed: true });
                        emit('finalfrage:setPhase', { phase: 'revealPicks' });
                    },
                };
            case 'revealPicks':
                return {
                    label: 'Lösung anzeigen',
                    onClick: () => {
                        emit('finalfrage:revealSolution', { revealed: true });
                        emit('finalfrage:setPhase', { phase: 'revealSolution' });
                    },
                };
            case 'revealSolution':
                return { label: 'Punkte verrechnen', onClick: () => emit('finalfrage:resolve') };
            case 'resolved':
                return { label: 'Finale beenden', onClick: () => emit('finalfrage:toggle', { active: false }) };
            default:
                return null;
        }
    })();

    return (
        <div className="card section-stack ff-regie">
            <div className="card-header">
                <h3 className="card-title">Finalfrage</h3>
                <button
                    type="button"
                    className={`toggle-button ${active ? 'is-on' : ''}`}
                    role="switch"
                    aria-checked={active}
                    onClick={() => emit('finalfrage:toggle', { active: !active })}
                >
                    <span className="toggle-indicator" aria-hidden="true" />
                    <span className="toggle-label">Finale aktiv</span>
                </button>
            </div>

            <div className="ff-regie-status">
                {active && live ? `Phase: ${PHASE_LABEL[phase] || phase} · Antworten ${revealedCount} / ${answers.length}` : 'Finale nicht aktiv'}
            </div>

            {primaryAction && (
                <div className="button-row">
                    <Button variant="primary" onClick={primaryAction.onClick}>{primaryAction.label}</Button>
                </div>
            )}

            {active && live && (
                <>
                    {/* Team-Farben */}
                    <div className="ff-regie-group">
                        <span className="ff-regie-group-title">Team-Farben — automatisch verteilt, korrigierbar</span>
                        <div className="ff-regie-colors">
                            {perTeamIds.map((id) => (
                                <label key={id} className="ff-regie-color">
                                    <input
                                        type="color"
                                        value={live.teamColors?.[id] || '#888888'}
                                        onChange={(e) => emit('finalfrage:setTeamColor', { teamId: id, color: e.target.value })}
                                    />
                                    {teamName(id)}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Frage & Antworten */}
                    <div className="ff-regie-group">
                        <span className="ff-regie-group-title">Frage &amp; Antworten</span>
                        {question.text && <div style={{ fontWeight: 600 }}>{question.text}</div>}
                        <div className="ff-regie-answers">
                            {answers.map((text, idx) => {
                                const shown = idx < revealedCount;
                                const isCorrect = showSolution && idx === question.correctIndex;
                                const pickers = perTeamIds.filter((id) => live.perTeam[id]?.pick === idx);
                                return (
                                    <div
                                        key={idx}
                                        className={`ff-regie-answer ${shown ? 'shown' : 'hidden'}${isCorrect ? ' correct' : ''}`}
                                    >
                                        <span className="ff-regie-answer-text">
                                            {LETTERS[idx] || idx + 1}. {text || <em>(leer)</em>}
                                        </span>
                                        <span className="ff-regie-answer-picks">
                                            {pickers.map((id) => (
                                                <i
                                                    key={id}
                                                    title={teamName(id)}
                                                    style={{ background: live.teamColors?.[id] || 'var(--muted)' }}
                                                />
                                            ))}
                                        </span>
                                        <span className="tag">{shown ? 'sichtbar' : 'verborgen'}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <span className="text-muted" style={{ fontSize: '0.8em' }}>Farbpunkte = aktuelle Tipps der Teams (live).</span>
                        <div className="ff-regie-btn-row">
                            <Button size="sm" onClick={() => emit('finalfrage:revealAnswer')}>Nächste Antwort zeigen</Button>
                            <Button size="sm" variant="surface" onClick={() => emit('finalfrage:revealAllAnswers')}>Alle zeigen</Button>
                            <Button
                                size="sm"
                                variant={showSolution ? 'primary' : 'ghost'}
                                onClick={() => setShowSolution((v) => !v)}
                            >
                                {showSolution ? 'Lösung ausblenden' : 'Lösung einblenden'}
                            </Button>
                        </div>
                    </div>

                    {/* Countdownsteuerung */}
                    <div className="ff-regie-group">
                        <span className="ff-regie-group-title">Countdownsteuerung</span>
                        <div className="ff-regie-btn-row">
                            <Button
                                size="sm"
                                variant={countdownRunning ? 'surface' : 'primary'}
                                onClick={() => emit('finalfrage:startCountdown')}
                            >
                                Start ({finale.config?.countdownSeconds ?? 20} s)
                            </Button>
                            <Button size="sm" variant="surface" disabled={!countdownRunning} onClick={() => emit('finalfrage:stopCountdown')}>Stopp</Button>
                            <Button size="sm" variant="ghost" disabled={!countdownRunning} onClick={() => emit('finalfrage:extendCountdown', { seconds: 10 })}>+10 s</Button>
                        </div>
                    </div>

                    {/* Aufdecken */}
                    <div className="ff-regie-group">
                        <span className="ff-regie-group-title">Aufdecken</span>
                        <div className="ff-regie-btn-row">
                            {perTeamIds.map((id) => {
                                const revealed = live.perTeam[id]?.revealed;
                                return (
                                    <Button
                                        key={id}
                                        size="sm"
                                        variant={revealed ? 'surface' : 'primary'}
                                        onClick={() => emit('finalfrage:revealTeamPick', { teamId: id, revealed: !revealed })}
                                    >
                                        {teamName(id)} {revealed ? 'verbergen' : 'aufdecken'}
                                    </Button>
                                );
                            })}
                            <Button size="sm" variant="surface" onClick={() => emit('finalfrage:revealAllPicks', { revealed: true })}>Alle</Button>
                            <Button
                                size="sm"
                                variant="surface"
                                onClick={() => emit('finalfrage:revealSolution', { revealed: !live.solutionRevealed })}
                            >
                                {live.solutionRevealed ? 'Lösung verbergen' : 'Lösung aufdecken'}
                            </Button>
                        </div>
                    </div>

                    {/* Abschluss */}
                    <div className="ff-regie-group">
                        <span className="ff-regie-group-title">Abschluss</span>
                        <div className="ff-regie-btn-row">
                            <Button size="sm" variant="primary" onClick={() => emit('finalfrage:resolve')}>Punkte verrechnen</Button>
                            {finale.reserveExists && (
                                <Button size="sm" variant="ghost" onClick={() => emit('finalfrage:startRound', { useReserve: true })}>Reserve-Frage laden</Button>
                            )}
                            <Button size="sm" variant="danger" onClick={() => emit('finalfrage:toggle', { active: false })}>Finale beenden</Button>
                        </div>
                    </div>

                    {/* Korrekturen */}
                    <details className="ff-regie-corrections" open={showCorrections} onToggle={(e) => setShowCorrections(e.target.open)}>
                        <summary>Korrekturen (Einsatz &amp; Antwort je Team überschreiben)</summary>
                        {perTeamIds.map((id) => {
                            const currentWager = live.perTeam[id]?.wager ?? 0;
                            const draft = wagerInputs[id];
                            const commitWager = () => {
                                if (draft === undefined) return;
                                if (draft !== '') {
                                    emit('finalfrage:overrideWager', { teamId: id, value: Math.max(0, Number(draft) || 0) });
                                }
                                setWagerInputs((p) => ({ ...p, [id]: undefined })); // zurück auf Serverwert
                            };
                            return (
                                <div key={id} className="ff-corr-row">
                                    <span className="lbl">{teamName(id)}</span>
                                    <input
                                        type="number"
                                        min="0"
                                        value={draft === undefined ? String(currentWager) : draft}
                                        onChange={(e) => setWagerInputs((p) => ({ ...p, [id]: e.target.value }))}
                                        onBlur={commitWager}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { commitWager(); e.currentTarget.blur(); } }}
                                    />
                                    <Button size="sm" variant="ghost" onClick={commitWager}>Übernehmen</Button>
                                    <select
                                        value={live.perTeam[id]?.pick ?? ''}
                                        onChange={(e) => emit('finalfrage:overridePick', { teamId: id, answerIndex: e.target.value === '' ? null : Number(e.target.value) })}
                                    >
                                        <option value="">— Antwort —</option>
                                        {answers.map((a, i) => (
                                            <option key={i} value={i}>{LETTERS[i] || i + 1}. {a}</option>
                                        ))}
                                    </select>
                                </div>
                            );
                        })}
                        <div className="ff-corr-row">
                            <Button size="sm" variant="danger" onClick={() => emit('finalfrage:resetLive')}>Runde abbrechen</Button>
                        </div>
                    </details>
                </>
            )}
        </div>
    );
}

export default FinaleFinalfrageRegie;
